import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

// ── Question Definitions by Entity Type ─────────────────────────
const ORGANIZER_QUESTIONS = {
  '1040': [
    {
      section: 'Personal Information',
      icon: '👤',
      questions: [
        { id: 'filing_status', label: 'Filing Status', type: 'select', options: ['Single', 'Married Filing Jointly', 'Married Filing Separately', 'Head of Household', 'Qualifying Widow(er)'] },
        { id: 'dependents', label: 'Do you have any dependents?', type: 'yesno' },
        { id: 'dependents_list', label: 'List dependents (name, DOB, relationship)', type: 'textarea', showIf: { id: 'dependents', value: 'yes' } },
        { id: 'address_changed', label: 'Did your address change this year?', type: 'yesno' },
        { id: 'new_address', label: 'New address', type: 'text', showIf: { id: 'address_changed', value: 'yes' } },
      ]
    },
    {
      section: 'Income',
      icon: '💵',
      questions: [
        { id: 'w2_income', label: 'Did you receive W-2 wages?', type: 'yesno' },
        { id: 'w2_employers', label: 'How many W-2s did you receive?', type: 'number', showIf: { id: 'w2_income', value: 'yes' } },
        { id: 'freelance_income', label: 'Did you have freelance or self-employment income?', type: 'yesno' },
        { id: 'freelance_amount', label: 'Approximate self-employment income ($)', type: 'number', showIf: { id: 'freelance_income', value: 'yes' } },
        { id: 'interest_dividends', label: 'Did you receive interest or dividend income (1099-INT/DIV)?', type: 'yesno' },
        { id: 'retirement_income', label: 'Did you receive retirement or pension income (1099-R)?', type: 'yesno' },
        { id: 'social_security', label: 'Did you receive Social Security benefits?', type: 'yesno' },
        { id: 'rental_income', label: 'Do you have rental property income?', type: 'yesno' },
        { id: 'rental_details', label: 'Rental property address and net income/loss', type: 'textarea', showIf: { id: 'rental_income', value: 'yes' } },
        { id: 'sold_investments', label: 'Did you sell any stocks, crypto, or investments?', type: 'yesno' },
      ]
    },
    {
      section: 'Deductions',
      icon: '📋',
      questions: [
        { id: 'home_owner', label: 'Do you own a home?', type: 'yesno' },
        { id: 'mortgage_interest', label: 'Did you pay mortgage interest (Form 1098)?', type: 'yesno', showIf: { id: 'home_owner', value: 'yes' } },
        { id: 'charitable', label: 'Did you make charitable contributions?', type: 'yesno' },
        { id: 'charitable_amount', label: 'Total charitable contributions ($)', type: 'number', showIf: { id: 'charitable', value: 'yes' } },
        { id: 'medical_expenses', label: 'Did you have significant medical expenses?', type: 'yesno' },
        { id: 'student_loan', label: 'Did you pay student loan interest?', type: 'yesno' },
        { id: 'hsa', label: 'Do you have a Health Savings Account (HSA)?', type: 'yesno' },
        { id: 'home_office', label: 'Do you use part of your home for business?', type: 'yesno' },
      ]
    },
    {
      section: 'Other',
      icon: '📎',
      questions: [
        { id: 'estimated_taxes', label: 'Did you make estimated tax payments?', type: 'yesno' },
        { id: 'estimated_amount', label: 'Total estimated tax payments ($)', type: 'number', showIf: { id: 'estimated_taxes', value: 'yes' } },
        { id: 'foreign_accounts', label: 'Do you have foreign bank accounts or assets?', type: 'yesno' },
        { id: 'irs_notices', label: 'Did you receive any IRS or state notices?', type: 'yesno' },
        { id: 'irs_notice_details', label: 'Describe the notice(s)', type: 'textarea', showIf: { id: 'irs_notices', value: 'yes' } },
        { id: 'additional_info', label: 'Anything else your accountant should know?', type: 'textarea' },
      ]
    }
  ],

  'schedule_c': [
    {
      section: 'Business Information',
      icon: '🏢',
      questions: [
        { id: 'business_name', label: 'Business name', type: 'text' },
        { id: 'business_type', label: 'Type of business / service', type: 'text' },
        { id: 'ein', label: 'EIN (if applicable)', type: 'text' },
        { id: 'business_start', label: 'Year business started', type: 'number' },
        { id: 'accounting_method', label: 'Accounting method', type: 'select', options: ['Cash', 'Accrual'] },
      ]
    },
    {
      section: 'Income',
      icon: '💵',
      questions: [
        { id: 'gross_receipts', label: 'Gross receipts / revenue ($)', type: 'number' },
        { id: 'returns_allowances', label: 'Returns and allowances ($)', type: 'number' },
        { id: 'cost_of_goods', label: 'Did you have cost of goods sold?', type: 'yesno' },
        { id: 'cogs_amount', label: 'Cost of goods sold ($)', type: 'number', showIf: { id: 'cost_of_goods', value: 'yes' } },
      ]
    },
    {
      section: 'Expenses',
      icon: '💳',
      questions: [
        { id: 'advertising', label: 'Advertising & marketing ($)', type: 'number' },
        { id: 'vehicle_use', label: 'Did you use a vehicle for business?', type: 'yesno' },
        { id: 'vehicle_miles', label: 'Business miles driven', type: 'number', showIf: { id: 'vehicle_use', value: 'yes' } },
        { id: 'home_office', label: 'Did you use a home office?', type: 'yesno' },
        { id: 'home_office_sqft', label: 'Home office square footage', type: 'number', showIf: { id: 'home_office', value: 'yes' } },
        { id: 'total_home_sqft', label: 'Total home square footage', type: 'number', showIf: { id: 'home_office', value: 'yes' } },
        { id: 'supplies', label: 'Office supplies ($)', type: 'number' },
        { id: 'software', label: 'Software & subscriptions ($)', type: 'number' },
        { id: 'professional_fees', label: 'Professional fees (legal, accounting) ($)', type: 'number' },
        { id: 'insurance', label: 'Business insurance ($)', type: 'number' },
        { id: 'other_expenses', label: 'Other business expenses (describe)', type: 'textarea' },
      ]
    },
    {
      section: 'Other',
      icon: '📎',
      questions: [
        { id: 'estimated_taxes', label: 'Did you make estimated tax payments?', type: 'yesno' },
        { id: 'estimated_amount', label: 'Total estimated payments ($)', type: 'number', showIf: { id: 'estimated_taxes', value: 'yes' } },
        { id: 'new_equipment', label: 'Did you purchase equipment over $500?', type: 'yesno' },
        { id: 'equipment_details', label: 'Describe equipment purchased', type: 'textarea', showIf: { id: 'new_equipment', value: 'yes' } },
        { id: 'additional_info', label: 'Anything else your accountant should know?', type: 'textarea' },
      ]
    }
  ],

  '1120S': [
    {
      section: 'Company Information',
      icon: '🏢',
      questions: [
        { id: 'company_name', label: 'Legal company name', type: 'text' },
        { id: 'ein', label: 'EIN', type: 'text' },
        { id: 'state_incorporated', label: 'State of incorporation', type: 'text' },
        { id: 'fiscal_year_end', label: 'Fiscal year end (MM/DD)', type: 'text' },
        { id: 's_election_date', label: 'S-Corp election date', type: 'text' },
      ]
    },
    {
      section: 'Shareholders',
      icon: '👥',
      questions: [
        { id: 'shareholder_count', label: 'Number of shareholders', type: 'number' },
        { id: 'shareholder_details', label: 'List shareholders (name, SSN/EIN, ownership %)', type: 'textarea' },
        { id: 'ownership_changed', label: 'Did ownership percentages change this year?', type: 'yesno' },
        { id: 'distributions', label: 'Total distributions paid to shareholders ($)', type: 'number' },
        { id: 'officer_compensation', label: 'Total officer/shareholder compensation ($)', type: 'number' },
      ]
    },
    {
      section: 'Income & Expenses',
      icon: '💵',
      questions: [
        { id: 'gross_receipts', label: 'Gross receipts / revenue ($)', type: 'number' },
        { id: 'cost_of_goods', label: 'Cost of goods sold ($)', type: 'number' },
        { id: 'total_expenses', label: 'Total operating expenses ($)', type: 'number' },
        { id: 'net_income', label: 'Estimated net income/loss ($)', type: 'number' },
      ]
    },
    {
      section: 'Balance Sheet & Other',
      icon: '📊',
      questions: [
        { id: 'total_assets', label: 'Total assets at year end ($)', type: 'number' },
        { id: 'total_liabilities', label: 'Total liabilities at year end ($)', type: 'number' },
        { id: 'loans_to_shareholders', label: 'Any loans to/from shareholders?', type: 'yesno' },
        { id: 'loan_details', label: 'Loan details and balances', type: 'textarea', showIf: { id: 'loans_to_shareholders', value: 'yes' } },
        { id: 'new_equipment', label: 'Did you purchase equipment or assets?', type: 'yesno' },
        { id: 'additional_info', label: 'Anything else your accountant should know?', type: 'textarea' },
      ]
    }
  ],

  '1065': [
    {
      section: 'Partnership Information',
      icon: '🤝',
      questions: [
        { id: 'partnership_name', label: 'Partnership name', type: 'text' },
        { id: 'ein', label: 'EIN', type: 'text' },
        { id: 'partner_count', label: 'Number of partners', type: 'number' },
        { id: 'partner_details', label: 'List partners (name, SSN/EIN, ownership %)', type: 'textarea' },
        { id: 'ownership_changed', label: 'Did any partner ownership change?', type: 'yesno' },
      ]
    },
    {
      section: 'Income & Distributions',
      icon: '💵',
      questions: [
        { id: 'gross_receipts', label: 'Gross receipts / revenue ($)', type: 'number' },
        { id: 'total_expenses', label: 'Total operating expenses ($)', type: 'number' },
        { id: 'guaranteed_payments', label: 'Guaranteed payments to partners ($)', type: 'number' },
        { id: 'distributions', label: 'Total distributions to partners ($)', type: 'number' },
        { id: 'capital_changes', label: 'Describe any capital account changes', type: 'textarea' },
      ]
    },
    {
      section: 'Other',
      icon: '📎',
      questions: [
        { id: 'foreign_partners', label: 'Are any partners foreign persons or entities?', type: 'yesno' },
        { id: 'new_assets', label: 'Did the partnership purchase any assets?', type: 'yesno' },
        { id: 'additional_info', label: 'Anything else your accountant should know?', type: 'textarea' },
      ]
    }
  ],

  '1120': [
    {
      section: 'Corporate Information',
      icon: '🏢',
      questions: [
        { id: 'company_name', label: 'Legal company name', type: 'text' },
        { id: 'ein', label: 'EIN', type: 'text' },
        { id: 'state_incorporated', label: 'State of incorporation', type: 'text' },
        { id: 'fiscal_year_end', label: 'Fiscal year end (MM/DD)', type: 'text' },
      ]
    },
    {
      section: 'Income',
      icon: '💵',
      questions: [
        { id: 'gross_receipts', label: 'Gross receipts ($)', type: 'number' },
        { id: 'cost_of_goods', label: 'Cost of goods sold ($)', type: 'number' },
        { id: 'dividends_received', label: 'Dividends received ($)', type: 'number' },
        { id: 'interest_income', label: 'Interest income ($)', type: 'number' },
      ]
    },
    {
      section: 'Deductions',
      icon: '📋',
      questions: [
        { id: 'officer_compensation', label: 'Officers compensation ($)', type: 'number' },
        { id: 'salaries', label: 'Other salaries & wages ($)', type: 'number' },
        { id: 'repairs', label: 'Repairs & maintenance ($)', type: 'number' },
        { id: 'taxes_licenses', label: 'Taxes & licenses ($)', type: 'number' },
        { id: 'charitable', label: 'Charitable contributions ($)', type: 'number' },
        { id: 'depreciation', label: 'Depreciation ($)', type: 'number' },
        { id: 'nol_carryforward', label: 'Prior year NOL carryforward ($)', type: 'number' },
      ]
    },
    {
      section: 'Other',
      icon: '📎',
      questions: [
        { id: 'dividends_paid', label: 'Dividends paid to shareholders ($)', type: 'number' },
        { id: 'estimated_taxes', label: 'Estimated tax deposits made ($)', type: 'number' },
        { id: 'additional_info', label: 'Anything else your accountant should know?', type: 'textarea' },
      ]
    }
  ],

  '990': [
    {
      section: 'Organization Information',
      icon: '🏛️',
      questions: [
        { id: 'org_name', label: 'Organization legal name', type: 'text' },
        { id: 'ein', label: 'EIN', type: 'text' },
        { id: 'mission', label: 'Mission statement', type: 'textarea' },
        { id: 'tax_exempt_status', label: 'Tax-exempt status', type: 'select', options: ['501(c)(3)', '501(c)(4)', '501(c)(6)', 'Other'] },
        { id: 'fiscal_year_end', label: 'Fiscal year end (MM/DD)', type: 'text' },
      ]
    },
    {
      section: 'Revenue',
      icon: '💵',
      questions: [
        { id: 'contributions', label: 'Total contributions & grants ($)', type: 'number' },
        { id: 'program_revenue', label: 'Program service revenue ($)', type: 'number' },
        { id: 'investment_income', label: 'Investment income ($)', type: 'number' },
        { id: 'other_revenue', label: 'Other revenue ($)', type: 'number' },
      ]
    },
    {
      section: 'Expenses & Programs',
      icon: '📋',
      questions: [
        { id: 'program_expenses', label: 'Program service expenses ($)', type: 'number' },
        { id: 'management_expenses', label: 'Management & general expenses ($)', type: 'number' },
        { id: 'fundraising_expenses', label: 'Fundraising expenses ($)', type: 'number' },
        { id: 'program_accomplishments', label: 'Describe 3 largest program accomplishments', type: 'textarea' },
        { id: 'officer_compensation', label: 'Were any officers/directors compensated over $100k?', type: 'yesno' },
        { id: 'officer_details', label: 'List officers and compensation', type: 'textarea', showIf: { id: 'officer_compensation', value: 'yes' } },
      ]
    },
    {
      section: 'Other',
      icon: '📎',
      questions: [
        { id: 'grant_activities', label: 'Did the org make any grants?', type: 'yesno' },
        { id: 'grant_details', label: 'Describe grant activities and recipients', type: 'textarea', showIf: { id: 'grant_activities', value: 'yes' } },
        { id: 'foreign_activities', label: 'Any foreign activities or accounts?', type: 'yesno' },
        { id: 'additional_info', label: 'Anything else your accountant should know?', type: 'textarea' },
      ]
    }
  ]
}

const ENTITY_LABELS = { '1040': '1040 — Personal', '1120S': '1120S — S-Corp', '1065': '1065 — Partnership', '1120': '1120 — C-Corp', 'schedule_c': 'Schedule C — Sole Prop', '990': '990 — Nonprofit' }

function QuestionField({ question, value, onChange }) {
  const baseStyle = { width: '100%' }

  if (question.type === 'yesno') return (
    <div style={{ display: 'flex', gap: 10 }}>
      {['yes', 'no'].map(opt => (
        <button key={opt} type="button" onClick={() => onChange(opt)} style={{
          flex: 1, padding: '10px', borderRadius: 8, border: '2px solid',
          fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
          borderColor: value === opt ? '#CBB17B' : '#DED6CA',
          background: value === opt ? '#F1E8D7' : '#fff',
          color: value === opt ? '#765B2C' : '#62615D',
        }}>{opt === 'yes' ? '✓ Yes' : '✗ No'}</button>
      ))}
    </div>
  )

  if (question.type === 'select') return (
    <select className="input" style={baseStyle} value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">Select…</option>
      {question.options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  )

  if (question.type === 'textarea') return (
    <textarea className="input" style={{ ...baseStyle, minHeight: 80, resize: 'vertical' }}
      placeholder="Type your answer here…" value={value || ''} onChange={e => onChange(e.target.value)} />
  )

  if (question.type === 'number') return (
    <input className="input" type="number" style={baseStyle}
      placeholder="0" value={value || ''} onChange={e => onChange(e.target.value)} />
  )

  return (
    <input className="input" type="text" style={baseStyle}
      placeholder="Type your answer here…" value={value || ''} onChange={e => onChange(e.target.value)} />
  )
}

export default function OrganizerPage() {
  const { profile, user, isCPA } = useAuth()
  const [clients, setClients] = useState([])
  const [selectedClientId, setSelectedClientId] = useState('')
  const [clientData, setClientData] = useState(null)
  const [organizer, setOrganizer] = useState(null)
  const [responses, setResponses] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeSection, setActiveSection] = useState(0)
  const [toast, setToast] = useState('')

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000) }

  useEffect(() => {
    async function load() {
      if (isCPA) {
        const { data: c } = await supabase.from('clients').select('*').eq('cpa_id', profile.id)
        setClients(c || [])
        setLoading(false)
      } else {
        const { data: client } = await supabase.from('clients').select('*').eq('portal_user_id', profile?.id || user?.id).maybeSingle()
        if (client) {
          setClientData(client)
          setSelectedClientId(client.id)
          await loadOrganizer(client.id, client.entity_type)
        }
        setLoading(false)
      }
    }
    load()
  }, [])

  async function loadOrganizer(clientId, entityType) {
    const { data } = await supabase.from('organizers').select('*').eq('client_id', clientId).maybeSingle()
    if (data) {
      setOrganizer(data)
      setResponses(data.responses || {})
    } else {
      // Create organizer if it doesn't exist
      const { data: newOrg } = await supabase.from('organizers').insert({
        client_id: clientId,
        tax_year: new Date().getFullYear(),
        entity_type: entityType,
        status: 'not_started',
        responses: {}
      }).select().single()
      setOrganizer(newOrg)
      setResponses({})
    }
  }

  async function handleClientSelect(clientId) {
    setSelectedClientId(clientId)
    setActiveSection(0)
    if (!clientId) { setClientData(null); setOrganizer(null); setResponses({}); return }
    const client = clients.find(c => c.id === clientId)
    setClientData(client)
    setLoading(true)
    await loadOrganizer(clientId, client.entity_type)
    setLoading(false)
  }

  function handleAnswer(questionId, value) {
    setResponses(prev => ({ ...prev, [questionId]: value }))
  }

  async function handleSave(complete = false) {
    if (!organizer) return
    setSaving(true)
    const status = complete ? 'completed' : 'in_progress'
    const { data } = await supabase.from('organizers').update({
      responses,
      status,
      completed_at: complete ? new Date().toISOString() : null,
    }).eq('id', organizer.id).select().single()
    if (data) setOrganizer(data)
    setSaving(false)
    notify(complete ? 'Organizer submitted! Your accountant has been notified.' : 'Progress saved!')
  }

  function shouldShow(question) {
    if (!question.showIf) return true
    return responses[question.showIf.id] === question.showIf.value
  }

  const questions = clientData ? ORGANIZER_QUESTIONS[clientData.entity_type] || [] : []

  const totalQuestions = questions.flatMap(s => s.questions.filter(q => !q.showIf)).length
  const answeredQuestions = questions.flatMap(s => s.questions.filter(q => !q.showIf && responses[q.id] !== undefined && responses[q.id] !== '')).length
  const progressPct = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0

  if (loading) return (
    <div style={{ display: 'grid', gap: 14 }}>
      {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
    </div>
  )

  return (
    <div className="organizer-page-root">
      <style>{`
        @media (max-width: 768px) {
          .organizer-page-root .organizer-layout {
            grid-template-columns: 1fr !important;
          }
          .organizer-page-root .organizer-sidebar {
            position: static !important;
            top: auto !important;
          }
          .organizer-page-root .organizer-main-card {
            padding: 18px 14px !important;
          }
        }
      `}</style>
      {/* CPA client selector */}
      {isCPA && (
        <div className="card animate-fadeUp" style={{ padding: '18px 20px', marginBottom: 20 }}>
          <label className="label">Select Client to View / Edit Organizer</label>
          <select className="input" style={{ maxWidth: 360 }} value={selectedClientId} onChange={e => handleClientSelect(e.target.value)}>
            <option value="">Choose a client…</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.business_name || c.full_name} — {ENTITY_LABELS[c.entity_type]}</option>
            ))}
          </select>
        </div>
      )}

      {!clientData && (
        <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <h3 style={{ marginBottom: 8 }}>{isCPA ? 'Select a client to view their organizer' : 'No organizer found'}</h3>
          <p style={{ color: '#62615D', fontSize: 13 }}>{isCPA ? 'Choose a client from the dropdown above' : 'Contact your accountant to get set up'}</p>
        </div>
      )}

      {clientData && questions.length > 0 && (
        <div className="organizer-layout" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20, alignItems: 'start' }}>
          {/* Section nav */}
          <div className="card animate-fadeUp organizer-sidebar" style={{ padding: '16px 12px', position: 'sticky', top: 20 }}>
            <div style={{ marginBottom: 14, padding: '0 8px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#202326', marginBottom: 4 }}>
                {clientData.business_name || clientData.full_name}
              </div>
              <div style={{ fontSize: 11, color: '#62615D', marginBottom: 10 }}>{ENTITY_LABELS[clientData.entity_type]}</div>
              {/* Progress bar */}
              <div style={{ fontSize: 11, color: '#62615D', marginBottom: 4 }}>{progressPct}% complete</div>
              <div style={{ height: 6, background: '#F0EBE0', borderRadius: 3 }}>
                <div style={{ width: `${progressPct}%`, height: '100%', background: progressPct === 100 ? '#10B981' : '#CBB17B', borderRadius: 3, transition: 'width 0.4s ease' }} />
              </div>
              {/* Status badge */}
              <div style={{ marginTop: 10 }}>
                <span className={`badge badge-${organizer?.status === 'completed' ? 'success' : organizer?.status === 'in_progress' ? 'info' : 'neutral'}`}>
                  {organizer?.status === 'completed' ? '✅ Completed' : organizer?.status === 'in_progress' ? '📝 In Progress' : '⬜ Not Started'}
                </span>
              </div>
            </div>

            {questions.map((section, idx) => (
              <button key={idx} onClick={() => setActiveSection(idx)} style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                marginBottom: 2, textAlign: 'left', transition: 'all 0.13s',
                background: activeSection === idx ? 'rgba(201,168,76,0.13)' : 'transparent',
                color: activeSection === idx ? '#765B2C' : '#62615D',
                fontSize: 12, fontWeight: activeSection === idx ? 700 : 400,
              }}>
                <span style={{ fontSize: 16 }}>{section.icon}</span>
                {section.section}
              </button>
            ))}

            <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12, display: 'grid', gap: 6 }}>
              <button className="btn btn-ghost" style={{ fontSize: 12, width: '100%', justifyContent: 'center' }}
                onClick={() => handleSave(false)} disabled={saving}>
                {saving ? 'Saving…' : '💾 Save Progress'}
              </button>
              {organizer?.status !== 'completed' && (
                <button className="btn btn-primary" style={{ fontSize: 12, width: '100%', justifyContent: 'center' }}
                  onClick={() => handleSave(true)} disabled={saving}>
                  ✓ Submit Organizer
                </button>
              )}
            </div>
          </div>

          {/* Questions */}
          <div className="animate-fadeUp">
            {questions[activeSection] && (
              <div className="card organizer-main-card" style={{ padding: '24px 28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 28 }}>{questions[activeSection].icon}</span>
                  <h2 style={{ fontSize: 20, color: '#202326' }}>{questions[activeSection].section}</h2>
                </div>

                <div style={{ display: 'grid', gap: 20 }}>
                  {questions[activeSection].questions.filter(shouldShow).map(q => (
                    <div key={q.id}>
                      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#202326', marginBottom: 8 }}>
                        {q.label}
                      </label>
                      <QuestionField
                        question={q}
                        value={responses[q.id]}
                        onChange={val => handleAnswer(q.id, val)}
                      />
                    </div>
                  ))}
                </div>

                {/* Section navigation */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <button className="btn btn-ghost" onClick={() => setActiveSection(s => Math.max(0, s - 1))} disabled={activeSection === 0}>
                    ← Previous
                  </button>
                  <span style={{ fontSize: 12, color: '#62615D', alignSelf: 'center' }}>
                    Section {activeSection + 1} of {questions.length}
                  </span>
                  {activeSection < questions.length - 1
                    ? <button className="btn btn-primary" onClick={() => { handleSave(false); setActiveSection(s => s + 1) }}>
                        Next →
                      </button>
                    : <button className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving}>
                        ✓ Submit Organizer
                      </button>
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, background: '#202326', color: '#fff', padding: '11px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: '0 8px 28px rgba(0,0,0,0.22)', animation: 'toastUp 0.2s ease' }}>
          ✓ {toast}
        </div>
      )}
    </div>
  )
}
